import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import toast from "react-hot-toast";
import { formatDate } from "../../lib/dateFormat";

interface Session {
  id: string;
  title: string;
  type: string;
  start_at: string;
  end_at: string;
  team_id: string;
}

interface Athlete {
  id: string;
  email: string;
  full_name: string;
  profile_url?: string;
}

interface AttendanceRecord {
  id: string;
  athlete_id: string;
  status: "present" | "late" | "absent" | "excused";
  reason?: string;
  athlete?: Athlete;
}

interface AttendanceState {
  [athleteId: string]: {
    status: "present" | "late" | "absent" | "excused" | null;
    reason: string;
    saving: boolean;
  };
}

interface PresenceCatalogProps {
  teamId: string;
  clubId: string;
}

export default function PresenceCatalog({ teamId, clubId }: PresenceCatalogProps) {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>("");
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [attendance, setAttendance] = useState<AttendanceState>({});
  const [loading, setLoading] = useState(false);
  const [loadingAthletes, setLoadingAthletes] = useState(false);

  // Fetch sessions for team
  useEffect(() => {
    if (!teamId) return;

    async function fetchSessions() {
      try {
        const { data } = await api.get(`/sessions/team/${teamId}`);
        // Sort by date, most recent first
        const sorted = data.sort((a: Session, b: Session) =>
          new Date(b.start_at).getTime() - new Date(a.start_at).getTime()
        );
        setSessions(sorted);
        // Auto-select the most recent session
        if (sorted.length > 0 && !selectedSessionId) {
          setSelectedSessionId(sorted[0].id);
        }
      } catch (err) {
        console.error("Failed to fetch sessions:", err);
      }
    }
    fetchSessions();
  }, [teamId]);

  // Fetch athletes and existing attendance when session changes
  useEffect(() => {
    if (!selectedSessionId || !teamId) return;

    async function fetchData() {
      setLoadingAthletes(true);
      try {
        // Fetch team athletes
        const { data: athletesData } = await api.get(`/teams/${teamId}/athletes`);
        setAthletes(athletesData.map((a: any) => a.user || a));

        // Fetch existing attendance for this session
        const { data: attendanceData } = await api.get(`/sessions/${selectedSessionId}/attendance`);

        // Build initial attendance state
        const initialState: AttendanceState = {};
        athletesData.forEach((a: any) => {
          const user = a.user || a;
          const existing = attendanceData.find((att: AttendanceRecord) => att.athlete_id === user.id);
          initialState[user.id] = {
            status: existing?.status || null,
            reason: existing?.reason || "",
            saving: false,
          };
        });
        setAttendance(initialState);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        toast.error(t("errors.load_failed", "Failed to load data"));
      } finally {
        setLoadingAthletes(false);
      }
    }
    fetchData();
  }, [selectedSessionId, teamId, t]);

  // Save attendance for a single athlete
  const saveAttendance = useCallback(async (athleteId: string, status: "present" | "late" | "absent" | "excused", reason: string) => {
    if (!selectedSessionId) return;

    setAttendance(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], saving: true },
    }));

    try {
      await api.post(`/sessions/${selectedSessionId}/attendance`, {
        records: [{
          user_id: athleteId,
          status,
          reason: reason || null,
        }],
      });

      setAttendance(prev => ({
        ...prev,
        [athleteId]: { status, reason, saving: false },
      }));
    } catch (err) {
      console.error("Failed to save attendance:", err);
      toast.error(t("errors.save_failed", "Failed to save"));
      setAttendance(prev => ({
        ...prev,
        [athleteId]: { ...prev[athleteId], saving: false },
      }));
    }
  }, [selectedSessionId, t]);

  // Handle status change
  const handleStatusChange = (athleteId: string, status: "present" | "late" | "absent" | "excused") => {
    const current = attendance[athleteId];
    setAttendance(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], status },
    }));
    // Auto-save
    saveAttendance(athleteId, status, current?.reason || "");
  };

  // Handle reason change with debounce
  const handleReasonChange = (athleteId: string, reason: string) => {
    setAttendance(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], reason },
    }));
  };

  // Save reason on blur
  const handleReasonBlur = (athleteId: string) => {
    const current = attendance[athleteId];
    if (current?.status) {
      saveAttendance(athleteId, current.status, current.reason);
    }
  };

  // Mark all present
  const markAllPresent = async () => {
    if (!selectedSessionId) return;
    setLoading(true);

    try {
      const records = athletes.map(a => ({
        user_id: a.id,
        status: "present" as const,
      }));

      await api.post(`/sessions/${selectedSessionId}/attendance`, { records });

      // Update local state
      const newState: AttendanceState = {};
      athletes.forEach(a => {
        newState[a.id] = { status: "present", reason: "", saving: false };
      });
      setAttendance(newState);
      toast.success(t("coach.all_marked_present", "All marked as present"));
    } catch (err) {
      console.error("Failed to mark all present:", err);
      toast.error(t("errors.save_failed", "Failed to save"));
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary
  const summary = {
    present: Object.values(attendance).filter(a => a.status === "present").length,
    late: Object.values(attendance).filter(a => a.status === "late").length,
    absent: Object.values(attendance).filter(a => a.status === "absent").length,
    excused: Object.values(attendance).filter(a => a.status === "excused").length,
  };

  // Get selected session info
  const selectedSession = sessions.find(s => s.id === selectedSessionId);

  // Status button styles
  const getStatusButtonClass = (status: string, isActive: boolean) => {
    const base = "px-3 py-1.5 text-sm font-medium rounded-lg transition-all border";
    if (!isActive) {
      return `${base} bg-slate-700/30 border-slate-600/30 text-slate-400 hover:bg-slate-700/50`;
    }
    switch (status) {
      case "present":
        return `${base} bg-emerald-500/20 border-emerald-500/50 text-emerald-300 ring-2 ring-emerald-500/30`;
      case "late":
        return `${base} bg-amber-500/20 border-amber-500/50 text-amber-300 ring-2 ring-amber-500/30`;
      case "absent":
        return `${base} bg-red-500/20 border-red-500/50 text-red-300 ring-2 ring-red-500/30`;
      case "excused":
        return `${base} bg-slate-500/20 border-slate-500/50 text-slate-300 ring-2 ring-slate-500/30`;
      default:
        return base;
    }
  };

  return (
    <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-700/50 bg-slate-800/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              {t("coach.presence_catalog", "Presence Catalog")}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {t("coach.presence_subtitle", "Take attendance for your training sessions")}
            </p>
          </div>

          {/* Session selector */}
          <div className="flex items-center gap-3">
            <select
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
              <option value="">{t("coach.select_session", "Select session...")}</option>
              {sessions.map(session => (
                <option key={session.id} value={session.id}>
                  {session.title} - {formatDate(new Date(session.start_at))}
                </option>
              ))}
            </select>

            <button
              onClick={markAllPresent}
              disabled={!selectedSessionId || loading || athletes.length === 0}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-lg transition-all"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t("common.saving", "Saving...")}
                </span>
              ) : (
                t("coach.mark_all_present", "Mark All Present")
              )}
            </button>
          </div>
        </div>

        {/* Summary */}
        {selectedSessionId && (
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-slate-700/30">
            <span className="text-sm text-slate-400">{t("coach.summary", "Summary")}:</span>
            <span className="flex items-center gap-1.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-emerald-300">{summary.present}</span>
              <span className="text-slate-500">{t("attendance.present", "Present")}</span>
            </span>
            <span className="flex items-center gap-1.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              <span className="text-amber-300">{summary.late}</span>
              <span className="text-slate-500">{t("attendance.late", "Late")}</span>
            </span>
            <span className="flex items-center gap-1.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              <span className="text-red-300">{summary.absent}</span>
              <span className="text-slate-500">{t("attendance.absent", "Absent")}</span>
            </span>
            <span className="flex items-center gap-1.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-slate-500"></span>
              <span className="text-slate-300">{summary.excused}</span>
              <span className="text-slate-500">{t("attendance.excused", "Excused")}</span>
            </span>
          </div>
        )}
      </div>

      {/* Athletes list */}
      <div className="divide-y divide-slate-700/30">
        {loadingAthletes ? (
          <div className="px-6 py-12 text-center">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-slate-400 mt-3">{t("common.loading", "Loading...")}</p>
          </div>
        ) : !selectedSessionId ? (
          <div className="px-6 py-12 text-center">
            <svg className="w-12 h-12 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-slate-400">{t("coach.select_session_hint", "Select a session to take attendance")}</p>
          </div>
        ) : athletes.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <svg className="w-12 h-12 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-slate-400">{t("coach.no_athletes", "No athletes in this team")}</p>
          </div>
        ) : (
          athletes.map(athlete => {
            const state = attendance[athlete.id] || { status: null, reason: "", saving: false };
            const showReason = state.status === "absent" || state.status === "excused";

            return (
              <div key={athlete.id} className="px-6 py-4 hover:bg-slate-700/20 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Athlete info */}
                  <div className="flex items-center gap-3 sm:w-48">
                    {athlete.profile_url ? (
                      <img
                        src={athlete.profile_url}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {athlete.full_name?.charAt(0) || athlete.email?.charAt(0) || "?"}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">
                        {athlete.full_name || athlete.email?.split("@")[0]}
                      </p>
                      <p className="text-slate-500 text-sm truncate">{athlete.email}</p>
                    </div>
                  </div>

                  {/* Status buttons */}
                  <div className="flex flex-wrap items-center gap-2 flex-1">
                    <button
                      onClick={() => handleStatusChange(athlete.id, "present")}
                      disabled={state.saving}
                      className={getStatusButtonClass("present", state.status === "present")}
                    >
                      {t("attendance.present", "Present")}
                    </button>
                    <button
                      onClick={() => handleStatusChange(athlete.id, "late")}
                      disabled={state.saving}
                      className={getStatusButtonClass("late", state.status === "late")}
                    >
                      {t("attendance.late", "Late")}
                    </button>
                    <button
                      onClick={() => handleStatusChange(athlete.id, "absent")}
                      disabled={state.saving}
                      className={getStatusButtonClass("absent", state.status === "absent")}
                    >
                      {t("attendance.absent", "Absent")}
                    </button>
                    <button
                      onClick={() => handleStatusChange(athlete.id, "excused")}
                      disabled={state.saving}
                      className={getStatusButtonClass("excused", state.status === "excused")}
                    >
                      {t("attendance.excused", "Excused")}
                    </button>

                    {/* Saving indicator */}
                    {state.saving && (
                      <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>

                  {/* Reason input (shown for absent/excused) */}
                  {showReason && (
                    <div className="sm:w-64">
                      <input
                        type="text"
                        value={state.reason}
                        onChange={(e) => handleReasonChange(athlete.id, e.target.value)}
                        onBlur={() => handleReasonBlur(athlete.id)}
                        placeholder={t("coach.reason_placeholder", "Reason (optional)")}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Session info footer */}
      {selectedSession && (
        <div className="px-6 py-3 bg-slate-800/30 border-t border-slate-700/50 text-sm text-slate-400">
          <span className="font-medium text-slate-300">{selectedSession.title}</span>
          <span className="mx-2">|</span>
          <span>{new Date(selectedSession.start_at).toLocaleString()}</span>
          <span className="mx-2">-</span>
          <span>{new Date(selectedSession.end_at).toLocaleTimeString()}</span>
        </div>
      )}
    </div>
  );
}
